import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faMedal, faTrophy } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { getEloColor } from '../utils/ladder';
import EloHistoryChart from '../components/EloHistoryChart';
import logo from '../assets/logo.png';

interface LeaderboardPlayer {
  id: number;
  display_name: string;
  avatar_url: string;
  is_bot: boolean;
  is_paused?: boolean;
  level_value: number;
  elo_rating: number;
  elo_games: number;
  wins: number;
  losses: number;
  avg_weekly_points: number;
}

interface LeaderboardResponse {
  ranking: LeaderboardPlayer[];
  me: { rank: number; elo_rating: number; elo_games: number };
}

interface Props {
  onBack: () => void;
}

export default function LeaderboardPage({ onBack }: Props) {
  const { token, playerId } = useAuth();
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);

  useEffect(() => {
    if (!token) return;
    api.get('/duel/leaderboard', token).then((res) => {
      if (res && res.ranking) setLeaderboard(res);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  // Default to logged-in player
  useEffect(() => {
    if (selectedPlayerId === null && playerId) {
      setSelectedPlayerId(playerId);
    }
  }, [playerId, selectedPlayerId]);

  const handlePlayerClick = useCallback((id: number) => {
    if (id === selectedPlayerId) return;
    setSelectedPlayerId(id);
  }, [selectedPlayerId]);

  if (loading) return <div className="loading">Loading leaderboard…</div>;

  return (
    <div className="archive-page archive-page--leaderboard">
      <header className="archive-header">
        <div className="archive-header-nav">
          <button className="btn-back" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
        </div>
        <img src={logo} alt="NoWeakWeek" className="archive-logo" />
        <h1>Leaderboard</h1>
      </header>

      {/* OnFire History Chart */}
      <EloHistoryChart selectedPlayerId={selectedPlayerId} mode="rank" />

      {leaderboard && leaderboard.ranking.length > 0 && (
        <div className="leaderboard-section">
          {leaderboard.me && (
            <div className="leaderboard-me">
              Your Rank: <strong>#{leaderboard.me.rank}</strong>
              {' — '}
              <span style={{ color: getEloColor(leaderboard.me.elo_rating) }}>
                🔥 {leaderboard.me.elo_rating} OnFire
              </span>
            </div>
          )}
          {/* Tabellen-Header */}
          <div className="leaderboard-table-header">
            <span className="leaderboard-col leaderboard-col--rank">#</span>
            <span className="leaderboard-col leaderboard-col--player"></span>
            <span className="leaderboard-col leaderboard-col--avg">∅ <FontAwesomeIcon icon={faTrophy} className="leaderboard-avg-icon" /></span>
            <span className="leaderboard-col leaderboard-col--games">W/L</span>
            <span className="leaderboard-col leaderboard-col--onfire">🔥</span>
          </div>
          <div className="leaderboard-table-list">
            {leaderboard.ranking.map((p, idx) => (
              <div
                key={p.id}
                className={`leaderboard-table-row${
                  leaderboard.me && leaderboard.me.rank === idx + 1 ? ' leaderboard-table-row--me' : ''
                }${
                  selectedPlayerId === p.id ? ' leaderboard-table-row--selected' : ''
                }`}
                onClick={() => handlePlayerClick(p.id)}
                style={{ cursor: 'pointer' }}
              >
                <span className="leaderboard-col leaderboard-col--rank">
                  {idx === 0 ? <FontAwesomeIcon icon={faMedal} className="leaderboard-medal leaderboard-medal--1" />
                  : idx === 1 ? <FontAwesomeIcon icon={faMedal} className="leaderboard-medal leaderboard-medal--2" />
                  : idx === 2 ? <FontAwesomeIcon icon={faMedal} className="leaderboard-medal leaderboard-medal--3" />
                  : <span className="leaderboard-rank-num">{idx + 1}</span>}
                </span>
                <span className="leaderboard-col leaderboard-col--player">
                  {p.avatar_url
                    ? <img src={p.avatar_url} alt="" className="leaderboard-avatar" />
                    : p.is_bot
                      ? <span className="leaderboard-avatar leaderboard-avatar--ph"><svg viewBox="0 0 640 512" width="24" height="24"><path fill="#888" d="M352 0c0-17.7-14.3-32-32-32S288-17.7 288 0l0 64-96 0c-53 0-96 43-96 96l0 224c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-224c0-53-43-96-96-96l-96 0 0-64zM160 368c0-13.3 10.7-24 24-24l32 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-32 0c-13.3 0-24-10.7-24-24zm120 0c0-13.3 10.7-24 24-24l32 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-32 0c-13.3 0-24-10.7-24-24zm120 0c0-13.3 10.7-24 24-24l32 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-32 0c-13.3 0-24-10.7-24-24zM224 176a48 48 0 1 1 0 96 48 48 0 1 1 0-96zm144 48a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zM64 224c0-17.7-14.3-32-32-32S0 206.3 0 224l0 96c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96zm544-32c-17.7 0-32 14.3-32 32l0 96c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96c0-17.7-14.3-32-32-32z"/></svg></span>
                      : <span className="leaderboard-avatar leaderboard-avatar--ph">{p.display_name.charAt(0)}</span>}
                  <span className="leaderboard-name">{p.display_name}</span>
                  {p.is_paused && <span className="player-paused-badge" title="pausiert – keine Strava-Verbindung">⏸</span>}
                </span>
                <span className="leaderboard-col leaderboard-col--avg">
                  {p.avg_weekly_points > 0 ? `${p.avg_weekly_points}` : ' —'}
                </span>
                <span className="leaderboard-col leaderboard-col--games">{p.wins}/{p.losses}</span>
                <span className="leaderboard-col leaderboard-col--onfire" style={{ color: getEloColor(p.elo_rating) }}>{p.elo_rating}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && (!leaderboard || leaderboard.ranking.length === 0) && (
        <p className="archive-empty">No leaderboard data yet.</p>
      )}
    </div>
  );
}
